import './assets/styles/critical.css';
import './App.css';
import logoWebp from './assets/images/a11y-services-logo.webp';
import logoPng from './assets/images/a11y-services-logo.png';
import { useState } from 'react';
import ResponsiveImage from './components/ResponsiveImage/ResponsiveImage';
import Navigation from './components/Navigation/Navigation';
import MobileMenu from './components/MobileMenu/MobileMenu';
import Popup from './components/Popup/Popup';
import CookieConsent from './components/CookieConsent/CookieConsent';
import FAQ from './components/FAQ/FAQ';
import SEOHead from './components/SEO/SEOHead';
import PipedriveForm from './components/PipedriveForm/PipedriveForm';
import { useHubSpot } from './hooks/useHubSpot';
import { generateFAQStructuredData, seoData } from './data/seoData';

const App = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [popup, setPopup] = useState(false);
  const { loadHubSpot, isLoaded } = useHubSpot();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleGetStarted = () => {
    if (localStorage.getItem('cookieConsent') === 'true') {
      setPopup(true);
    } else {
      const shouldRedirect = window.confirm(
        'Cookies are required to use our contact forms. Would you like to contact us directly via email instead?'
      );
      if (shouldRedirect) {
        window.location.href = 'mailto:support@cloudixmedia.com?subject=Get Started - Accessibility Services&body=Hi, I would like to get started with your accessibility services. Please contact me to discuss my needs.';
      }
    }
  };

  const handleScheduleConsultation = () => {
    if (localStorage.getItem('cookieConsent') === 'true') {
      setPopup(true);
    } else {
      const shouldRedirect = window.confirm(
        'Cookies are required to use our contact forms. Would you like to contact us directly via email instead?'
      );
      if (shouldRedirect) {
        window.location.href = 'mailto:support@cloudixmedia.com?subject=Schedule Consultation - Accessibility Services&body=Hi, I would like to schedule a free consultation to discuss my accessibility needs. Please let me know your availability.';
      }
    }
  };

  const handleCookieAccept = () => {
    loadHubSpot();
  };

  return (
    <>
      <SEOHead
        title="A11y Services - Professional Web Accessibility Consulting & WCAG Compliance"
        description="Expert web accessibility consulting services. WCAG 2.1 AA compliance audits, accessibility testing, remediation, and training. Make your website accessible to all users."
        keywords={seoData.site.keywords}
        canonicalUrl={seoData.site.url}
        structuredData={generateFAQStructuredData()}
      />
      <div className="min-h-screen bg-white">

        {/* Header */}
        <header className="bg-blue-900 text-white">
          <nav className="container mx-auto px-4 py-4" aria-label="Main navigation">
            <div className="flex items-center justify-between">
              <ResponsiveImage
                src={logoPng}
                webpSrc={logoWebp}
                alt="A11y Services - Web Accessibility Consulting"
                className="h-16 md:h-20 w-auto"
                sizes="(max-width: 768px) 64px, 80px"
                width={80}
                height={80}
                priority={true}
              />

              {/* Desktop Navigation */}

              <Navigation />

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer"
                onClick={toggleMobileMenu}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label="Toggle mobile menu"
                type="button"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>

            {/* Mobile Navigation Menu */}
            <MobileMenu isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />
          </nav>
        </header>

        {/* Hero Section */}
        <main id="main-content">
          <section
            className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12 md:py-20"
            aria-labelledby="hero-heading"
          >
            <div className="container mx-auto px-4 md:px-6 text-center">
              <h1 id="hero-heading" className="text-3xl md:text-5xl font-bold mb-4 md:mb-6">
                Making Digital Experiences Accessible for Everyone
              </h1>
              <p className="text-lg md:text-xl mb-6 md:mb-8 max-w-3xl mx-auto">
                We provide comprehensive accessibility services to ensure your digital products
                are usable by people of all abilities. WCAG compliant solutions that expand your reach.
              </p>
              <button
                className="bg-white text-blue-800 px-6 md:px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors focus:outline-none focus:ring-4 focus:ring-blue-300 cursor-pointer"
                aria-label="Get started with our accessibility services today"
                type="button"
                onClick={handleGetStarted}
              >
                Get Started Today
              </button>
            </div>
          </section>

          {/* Services Section */}
          <section
            id="services"
            className="py-12 md:py-20 bg-gray-50"
            aria-labelledby="services-heading"
          >
            <div className="container mx-auto px-4 md:px-6">
              <h2 id="services-heading" className="text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12 text-gray-800">
                Our Services
              </h2>
              <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                <article className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
                  <div
                    className="text-blue-600 text-4xl mb-4"
                    role="img"
                    aria-label="Magnifying glass icon representing accessibility audits"
                  >
                    🔍
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold mb-4">Accessibility Audits</h3>
                  <p className="text-gray-600">
                    Comprehensive evaluation of your digital products against WCAG 2.1 AA standards.
                    Detailed reports with actionable recommendations for improving accessibility.
                  </p>
                </article>
                <article className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
                  <div
                    className="text-blue-600 text-4xl mb-4"
                    role="img"
                    aria-label="Lightning bolt icon representing fast remediation services"
                  >
                    ⚡
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold mb-4">Remediation Services</h3>
                  <p className="text-gray-600">
                    Expert implementation of accessibility fixes. From code updates to design
                    improvements, we make your products truly inclusive for all users.
                  </p>
                </article>
                <article className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
                  <div
                    className="text-blue-600 text-4xl mb-4"
                    role="img"
                    aria-label="Graduation cap icon representing training and education services"
                  >
                    🎓
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold mb-4">Training & Consulting</h3>
                  <p className="text-gray-600">
                    Empower your team with accessibility knowledge. Custom training programs
                    and ongoing consultation to build inclusive development practices.
                  </p>
                </article>
              </div>
            </div>
          </section>

          {/* About Section */}
          <section
            id="about"
            className="py-12 md:py-20"
            aria-labelledby="about-heading"
          >
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h2 id="about-heading" className="text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-gray-800">
                  Why Accessibility Matters
                </h2>
                <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">
                  Over 1 billion people worldwide live with disabilities. By making your digital
                  products accessible, you're not just complying with legal requirements – you're
                  expanding your market reach and creating better experiences for everyone.
                </p>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-8 md:mt-12">
                  <article className="text-left">
                    <h3 className="text-xl md:text-2xl font-semibold mb-4 text-gray-800">Legal Compliance</h3>
                    <p className="text-gray-600">
                      Stay compliant with ADA, Section 508, and international accessibility laws.
                      Avoid costly lawsuits and ensure your business is protected from legal risks.
                    </p>
                  </article>
                  <article className="text-left">
                    <h3 className="text-xl md:text-2xl font-semibold mb-4 text-gray-800">Better UX for All</h3>
                    <p className="text-gray-600">
                      Accessible design principles improve usability for everyone. Clear navigation,
                      readable text, and intuitive interfaces benefit all users, not just those with disabilities.
                    </p>
                  </article>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <FAQ />

          {/* Contact Section */}
          <section
            id="contact"
            className="py-12 md:py-20 bg-blue-900 text-white"
            aria-labelledby="contact-heading"
          >
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-2xl mx-auto text-center">
                <h2 id="contact-heading" className="text-3xl md:text-4xl font-bold mb-6 md:mb-8">
                  Ready to Get Started?
                </h2>
                <p className="text-lg md:text-xl mb-6 md:mb-8">
                  Let's make your digital products accessible to everyone.
                  Contact us today for a free consultation.
                </p>
                <address className="space-y-4 not-italic" aria-label="Contact information">
                  <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
                    <span
                      className="text-2xl"
                      role="img"
                      aria-label="Email icon"
                    >
                      📧
                    </span>
                    <a
                      href="mailto:support@cloudixmedia.com?subject=Schedule Consultation - Accessibility Services&body=Hi, I would like to schedule a free consultation to discuss my accessibility needs. Please let me know your availability."
                      className="text-base md:text-lg hover:underline focus:outline-none focus:ring-2 focus:ring-blue-300 rounded px-2 py-1 break-all"
                      aria-label="Send email to hello at a11y services dot com"
                    >
                      support@cloudixmedia.com
                    </a>
                  </div>
                  {/*<div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <span
                    className="text-2xl"
                    role="img"
                    aria-label="Phone icon"
                  >
                    📞
                  </span>
                   <a
                    href="tel:+15551234567"
                    className="text-base md:text-lg hover:underline focus:outline-none focus:ring-2 focus:ring-blue-300 rounded px-2 py-1"
                    aria-label="Call us at +1 555 123 4567"
                  >
                    +1 (555) 123-4567
                  </a> 
                </div>*/}
                </address>
                <button
                  className="mt-6 md:mt-8 bg-white text-blue-900 px-6 md:px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors focus:outline-none focus:ring-4 focus:ring-blue-300 cursor-pointer"
                  aria-label="Schedule a free accessibility consultation with our team"
                  type="button"
                  onClick={handleScheduleConsultation}
                >
                  Schedule Consultation
                </button>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-6 md:py-8">
          <div className="container mx-auto px-4 md:px-6 text-center">
            <p className="text-sm md:text-base">
              &copy; 2025 A11y Services. Making the web accessible for everyone.
            </p>
            <p className="text-xs md:text-sm text-gray-400 mt-2">
              Powered by <a href="https://cloudixmedia.com" className="hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">cloudixmedia.com</a>
            </p>
          </div>
        </footer>
      </div>

      <Popup isOpen={popup} onClose={() => setPopup(false)} >
        <PipedriveForm />
      </Popup>

      <CookieConsent onAccept={handleCookieAccept} />
    </>
  );
};

export default App;
